import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import pl from 'date-fns/locale/pl';
import fr from 'date-fns/locale/fr';
import enUS from 'date-fns/locale/en-US';

// BackButton component
const BackButton = ({ language }) => {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate('/mecz/turniej-rankingowy')}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = ({ language }) => {
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('tournament', language);
        } else if (seg === 'mecze') {
          displayText = getTranslation('przeglad-meczow-turniejowych', language);
        } else if (seg === 'mecz') {
          return null;
        } else {
          displayText = seg;
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const TournamentMatches = () => {
  const { language } = useLanguage();
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [editData, setEditData] = useState({});
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    player2: '',
    player3: '',
    player4: '',
    opponent1: '',
    opponent2: '',
    opponent3: '',
    opponent4: '',
    dateFrom: '',
    dateTo: '',
    numberOfPlayers: '',
    roundtype: ''
  });

  // Fetch games data
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        
        if (!token) {
          throw new Error(getTranslation('brak-tokenu-autoryzacji', language));
        }
        
        const response = await fetch('/api/games', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`${getTranslation('blad', language)} ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        
        // Filter only tournament games with ranking roundtypes (G, P, B)
        const tournamentGames = data.filter(game => 
          game.tournament !== null && 
          ['G', 'P', 'B'].includes(game.roundtype)
        );
        
        setGames(tournamentGames);
        setFilteredGames(tournamentGames);
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = games.filter(game => {
      const gameDate = new Date(game.gamedate);
      const gameDateOnly = new Date(gameDate.getFullYear(), gameDate.getMonth(), gameDate.getDate());
      
      const dateFrom = filters.dateFrom ? new Date(filters.dateFrom + 'T00:00:00') : null;
      const dateTo = filters.dateTo ? new Date(filters.dateTo + 'T23:59:59') : null;
      
      return (
        (!filters.player2 || (game.player2 && game.player2.toLowerCase().includes(filters.player2.toLowerCase()))) &&
        (!filters.player3 || (game.player3 && game.player3.toLowerCase().includes(filters.player3.toLowerCase()))) &&
        (!filters.player4 || (game.player4 && game.player4.toLowerCase().includes(filters.player4.toLowerCase()))) &&
        (!filters.opponent1 || (game.opponent1 && game.opponent1.toLowerCase().includes(filters.opponent1.toLowerCase()))) &&
        (!filters.opponent2 || (game.opponent2 && game.opponent2.toLowerCase().includes(filters.opponent2.toLowerCase()))) &&
        (!filters.opponent3 || (game.opponent3 && game.opponent3.toLowerCase().includes(filters.opponent3.toLowerCase()))) &&
        (!filters.opponent4 || (game.opponent4 && game.opponent4.toLowerCase().includes(filters.opponent4.toLowerCase()))) &&
        (!dateFrom || gameDateOnly >= dateFrom) &&
        (!dateTo || gameDateOnly <= dateTo) &&
        (!filters.numberOfPlayers || game.numberofplayers.toString() === filters.numberOfPlayers) &&
        (!filters.roundtype || game.roundtype === filters.roundtype)
      );
    });
    
    setFilteredGames(filtered);
  }, [games, filters]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle edit start
  const handleEdit = (game) => {
    setEditingGame(game.game_id);
    setEditData({
      player2: game.player2 || '',
      player3: game.player3 || '',
      player4: game.player4 || '',
      opponent1: game.opponent1 || '',
      opponent2: game.opponent2 || '',
      opponent3: game.opponent3 || '',
      opponent4: game.opponent4 || '',
      playerscore: game.playerscore,
      opponentscore: game.opponentscore,
      roundtype: game.roundtype || ''
    });
  };

  // Handle edit data change
  const handleEditDataChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save
  const handleSave = async (gameId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      
      if (!token) {
        throw new Error(getTranslation('brak-tokenu-autoryzacji', language));
      }
      
      const response = await fetch(`/api/games/${gameId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${getTranslation('blad', language)} ${response.status}: ${errorText}`);
      }
      
      // Update the game in the local state
      setGames(prevGames => 
        prevGames.map(game => 
          game.game_id === gameId 
            ? { ...game, ...editData }
            : game
        )
      );
      
      setEditingGame(null);
      setEditData({});
      
    } catch (err) {
      console.error('Save error:', err);
      setError(`${getTranslation('blad-podczas-zapisywania', language)}: ${err.message}`);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingGame(null);
    setEditData({});
  };

  // Handle delete
  const handleDelete = async (gameId) => {
    setGameToDelete(gameId);
    setShowConfirmModal(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!gameToDelete) return;
    
    try {
      const token = sessionStorage.getItem('access_token');
      
      if (!token) {
        throw new Error(getTranslation('brak-tokenu-autoryzacji', language));
      }
      
      const response = await fetch(`/api/games/${gameToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${getTranslation('blad', language)} ${response.status}: ${errorText}`);
      }
      
      // Remove the game from the local state
      setGames(prevGames => prevGames.filter(game => game.game_id !== gameToDelete));
      
      // Close modal and reset state
      setShowConfirmModal(false);
      setGameToDelete(null);
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(`${getTranslation('blad-podczas-usuwania', language)}: ${err.message}`);
      setShowConfirmModal(false);
      setGameToDelete(null);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setGameToDelete(null);
  };

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <div style={{ fontSize: '24px', color: '#666' }}>{getTranslation('ladowanie-meczow', language)}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <div style={{ fontSize: '24px', color: '#d32f2f' }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        ShootMe
      </h1>
      
      {/* Back button and breadcrumbs */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton language={language} />
        <Breadcrumbs language={language} />
      </div>

      {/* Filters */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto 20px auto'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#1e3a8a', fontFamily: 'Nunito, Arial, sans-serif' }}>
          {getTranslation('filtry', language)}
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px' 
        }}>
          <input
            type="text"
            placeholder={getTranslation('gracz2', language)}
            value={filters.player2}
            onChange={(e) => handleFilterChange('player2', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('gracz3', language)}
            value={filters.player3}
            onChange={(e) => handleFilterChange('player3', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('rezerwowy', language)}
            value={filters.player4}
            onChange={(e) => handleFilterChange('player4', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('przeciwnik1', language)}
            value={filters.opponent1}
            onChange={(e) => handleFilterChange('opponent1', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('przeciwnik2', language)}
            value={filters.opponent2}
            onChange={(e) => handleFilterChange('opponent2', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('przeciwnik3', language)}
            value={filters.opponent3}
            onChange={(e) => handleFilterChange('opponent3', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('rezerwowy-przeciwnik', language)}
            value={filters.opponent4}
            onChange={(e) => handleFilterChange('opponent4', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'pl' ? pl : language === 'fr' ? fr : enUS}>
            <DatePicker
              label={getTranslation('data-od', language)}
              value={filters.dateFrom ? new Date(filters.dateFrom) : null}
              onChange={(date) => {
                const dateString = date ? date.toISOString().split('T')[0] : '';
                handleFilterChange('dateFrom', dateString);
              }}
              format={language === 'pl' ? 'dd/MM/yyyy' : language === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
              slotProps={{
                textField: {
                  placeholder: language === 'pl' ? 'dd/mm/rrrr' : language === 'fr' ? 'jj/mm/aaaa' : 'mm/dd/yyyy',
                  style: {
                    fontFamily: 'Nunito, Arial, sans-serif'
                  }
                }
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'pl' ? pl : language === 'fr' ? fr : enUS}>
            <DatePicker
              label={getTranslation('data-do', language)}
              value={filters.dateTo ? new Date(filters.dateTo) : null}
              onChange={(date) => {
                const dateString = date ? date.toISOString().split('T')[0] : '';
                handleFilterChange('dateTo', dateString);
              }}
              format={language === 'pl' ? 'dd/MM/yyyy' : language === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
              slotProps={{
                textField: {
                  placeholder: language === 'pl' ? 'dd/mm/rrrr' : language === 'fr' ? 'jj/mm/aaaa' : 'mm/dd/yyyy',
                  style: {
                    fontFamily: 'Nunito, Arial, sans-serif'
                  }
                }
              }}
            />
          </LocalizationProvider>
        </div>
        
        {/* Number of Players Radio Buttons */}
        <div style={{ 
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ 
            margin: '0 0 15px 0', 
            color: '#1e3a8a', 
            fontFamily: 'Nunito, Arial, sans-serif',
            fontSize: '16px'
          }}>
            {getTranslation('liczba-graczy', language)}
          </h4>
          <div style={{ 
            display: 'flex', 
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value=""
                checked={filters.numberOfPlayers === ''}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('wszystkie', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value="1"
                checked={filters.numberOfPlayers === '1'}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('single', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value="2"
                checked={filters.numberOfPlayers === '2'}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('dublety', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value="3"
                checked={filters.numberOfPlayers === '3'}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('triplety', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value="4"
                checked={filters.numberOfPlayers === '4'}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('triplety-z-rezerwowym', language)}
            </label>
          </div>
        </div>
        
        {/* Roundtype Radio Buttons */}
        <div style={{ 
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ 
            margin: '0 0 15px 0', 
            color: '#1e3a8a', 
            fontFamily: 'Nunito, Arial, sans-serif',
            fontSize: '16px'
          }}>
            {getTranslation('typ-rundy', language)}
          </h4>
          <div style={{ 
            display: 'flex', 
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="roundtype"
                value=""
                checked={filters.roundtype === ''}
                onChange={(e) => handleFilterChange('roundtype', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('wszystkie', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="roundtype"
                value="G"
                checked={filters.roundtype === 'G'}
                onChange={(e) => handleFilterChange('roundtype', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('grupowa', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="roundtype"
                value="P"
                checked={filters.roundtype === 'P'}
                onChange={(e) => handleFilterChange('roundtype', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('pucharowa', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="roundtype"
                value="B"
                checked={filters.roundtype === 'B'}
                onChange={(e) => handleFilterChange('roundtype', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('buchholz', language)}
            </label>
          </div>
        </div>
      </div>

      {/* Games Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
          <h3 style={{ margin: 0, color: '#333', fontFamily: 'Nunito, Arial, sans-serif' }}>
            {getTranslation('mecze-turniejowe', language)} ({filteredGames.length})
          </h3>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('data', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('typ-rundy', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('gracz2', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('gracz3', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('rezerwowy', language)}</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('wynik', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('przeciwnik1', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('przeciwnik2', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('przeciwnik3', language)}</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('rezerwowy-przeciwnik', language)}</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '12px', fontWeight: '600' }}>{getTranslation('akcje', language)}</th>
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((game) => (
                <tr key={game.game_id} style={{ borderBottom: '1px solid #eee' }}>
                  {/* Date */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {new Date(game.gamedate).toLocaleDateString('pl-PL')}
                  </td>
                  
                  {/* Roundtype */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <select
                        value={editData.roundtype}
                        onChange={(e) => handleEditDataChange('roundtype', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      >
                        <option value="G">{getTranslation('grupowa', language)}</option>
                        <option value="P">{getTranslation('pucharowa', language)}</option>
                        <option value="B">{getTranslation('buchholz', language)}</option>
                      </select>
                    ) : (
                      game.roundtype === 'G' ? getTranslation('grupowa', language) : 
                      game.roundtype === 'P' ? getTranslation('pucharowa', language) : 
                      game.roundtype === 'B' ? getTranslation('buchholz', language) : game.roundtype
                    )}
                  </td>
                  
                  {/* Player 2 */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.player2}
                        onChange={(e) => handleEditDataChange('player2', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.player2 || '-'
                    )}
                  </td>
                  
                  {/* Player 3 */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.player3}
                        onChange={(e) => handleEditDataChange('player3', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.player3 || '-'
                    )}
                  </td>
                  
                  {/* Player 4 (Rezerwowy) */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.player4}
                        onChange={(e) => handleEditDataChange('player4', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.player4 || '-'
                    )}
                  </td>
                  
                  {/* Score */}
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={editData.playerscore}
                          onChange={(e) => handleEditDataChange('playerscore', parseInt(e.target.value))}
                          style={{
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                            width: '50px'
                          }}
                        />
                        <span>:</span>
                        <input
                          type="number"
                          value={editData.opponentscore}
                          onChange={(e) => handleEditDataChange('opponentscore', parseInt(e.target.value))}
                          style={{
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                            width: '50px'
                          }}
                        />
                      </div>
                    ) : (
                      <span style={{ 
                        fontWeight: 'bold',
                        color: game.playerscore > game.opponentscore ? '#2e7d32' : 
                               game.playerscore < game.opponentscore ? '#c62828' : '#666'
                      }}>
                        {game.playerscore} : {game.opponentscore}
                      </span>
                    )}
                  </td>
                  
                  {/* Opponent 1 */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.opponent1}
                        onChange={(e) => handleEditDataChange('opponent1', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.opponent1 || '-'
                    )}
                  </td>
                  
                  {/* Opponent 2 */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.opponent2}
                        onChange={(e) => handleEditDataChange('opponent2', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.opponent2 || '-'
                    )}
                  </td>
                  
                  {/* Opponent 3 */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.opponent3}
                        onChange={(e) => handleEditDataChange('opponent3', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.opponent3 || '-'
                    )}
                  </td>
                  
                  {/* Opponent 4 (Rezerwowy przeciwnik) */}
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.opponent4}
                        onChange={(e) => handleEditDataChange('opponent4', e.target.value)}
                        style={{
                          padding: '4px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.opponent4 || '-'
                    )}
                  </td>
                  
                  {/* Actions */}
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {editingGame === game.game_id ? (
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleSave(game.game_id)}
                          style={{
                            background: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          style={{
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEdit(game)}
                          style={{
                            background: '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          {getTranslation('edytuj', language)}
                        </button>
                        <button
                          onClick={() => handleDelete(game.game_id)}
                          style={{
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          {getTranslation('usun', language)}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredGames.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            {getTranslation('brak-meczow-spelniajacych-kryteria', language)}
          </div>
        )}
      </div>
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {getTranslation('potwierdz-usuniecie', language)}
            </h3>
            <p style={{
              margin: '0 0 30px 0',
              color: '#666',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '16px',
              lineHeight: '1.5'
            }}>
              {getTranslation('czy-na-pewno-usunac-mecz', language)}
            </p>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCancelDelete}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  transition: 'background-color 0.2s',
                  minWidth: '100px'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
              >
                {getTranslation('anuluj', language)}
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  transition: 'background-color 0.2s',
                  minWidth: '100px'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
              >
                {getTranslation('usun', language)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentMatches;
